import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Download, Printer, ZoomIn, ZoomOut, Edit3, X, Check, Save } from 'lucide-react';
import MarksCardRenderer, { detectTemplateKey, PRESET_DEFAULTS } from '../../MarksCardRenderer';
import { reportCardService, ReportCardTemplateService } from '../../../services/index.js';
import { MARKS_CARD_FORMATS } from './ReportCards';

const ReportCardPreview = ({
  reportCard = null,
  initialFormat = 'classic_academic',
  orgData = {},
  onBack,
  onDownloadPDF
}) => {
  const [format, setFormat] = useState(initialFormat || 'classic_academic');
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dbTemplates, setDbTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSavedMsg, setTemplateSavedMsg] = useState('');

  // Live Remarks Edit Modal
  const [isEditingRemarks, setIsEditingRemarks] = useState(false);
  const [teacherRemarks, setTeacherRemarks] = useState('');
  const [principalRemarks, setPrincipalRemarks] = useState('');
  const [savingRemarks, setSavingRemarks] = useState(false);
  const [remarksMsg, setRemarksMsg] = useState('');

  // Fetch organization custom templates from DB so customized styles/headers render
  useEffect(() => {
    const orgId = orgData?.orgId || localStorage.getItem('orgId') || 'SCH-02';
    ReportCardTemplateService.getTemplatesByOrg(orgId)
      .then(res => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        setDbTemplates(list);
      })
      .catch(err => console.error('Failed to load org templates in preview:', err));
  }, [orgData?.orgId]);

  useEffect(() => {
    if (reportCard && reportCard.id) {
      loadDetails(reportCard.id);
    }
  }, [reportCard]);

  const loadDetails = async (id) => {
    setLoading(true);
    try {
      const res = await reportCardService.getReportCardById(id);
      setDetails(res);
      let detected = null;
      if (res?.reportCard?.templateConfigSnapshot) {
        let snap = res.reportCard.templateConfigSnapshot;
        if (typeof snap === 'string') {
          try { snap = JSON.parse(snap); } catch (e) { snap = null; }
        }
        detected = snap?.templateKey || (snap ? detectTemplateKey(snap) : null);
      }
      // If user came with an explicit initialFormat (e.g. from workspace template selector), prefer that
      setFormat(initialFormat || detected || 'classic_academic');
    } catch (err) {
      console.error('Failed to load report card details for full preview:', err);
    } finally {
      setLoading(false);
    }
  };

  const rc = details?.reportCard || reportCard;
  const entries = details?.entries || [];
  const branding = details?.branding || orgData || {};
  const orgInfo = details?.organizationInfo || orgData || {};
  const gradingScale = details?.gradingScale || [];
  const studentName = rc?.student?.name || rc?.studentName || 'Student';

  const cardSavedFormat = useMemo(() => {
    if (rc?.templateConfigSnapshot) {
      let snap = rc.templateConfigSnapshot;
      if (typeof snap === 'string') {
        try { snap = JSON.parse(snap); } catch (e) { snap = null; }
      }
      return snap?.templateKey || (snap ? detectTemplateKey(snap) : null);
    }
    return null;
  }, [rc]);

  const activeTemplateConfig = useMemo(() => {
    const dbMatch = dbTemplates.find(t => t.templateKey === format);
    if (dbMatch?.configJson) {
      try {
        return typeof dbMatch.configJson === 'string' ? JSON.parse(dbMatch.configJson) : dbMatch.configJson;
      } catch (e) {
        console.error('Error parsing template config:', e);
      }
    }
    return PRESET_DEFAULTS[format] || null;
  }, [dbTemplates, format]);

  const handleApplyTemplateToCard = async () => {
    if (!rc?.id) return;
    setSavingTemplate(true);
    try {
      await reportCardService.updateTemplate(rc.id, format);
      setTemplateSavedMsg('Format saved to card!');
      setTimeout(() => setTemplateSavedMsg(''), 3000);
      await loadDetails(rc.id);
    } catch (err) {
      alert('Failed to update template: ' + (err.message || err));
    } finally {
      setSavingTemplate(false);
    }
  };

  if (!reportCard) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{
      background: '#0f172a',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      
      {/* Top Sticky Toolbar */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: '#1e293b',
        borderBottom: '1px solid #334155',
        padding: '12px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        {/* Left: Back button & Student info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '7px',
              background: '#334155',
              color: '#ffffff',
              border: 'none',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            <ArrowLeft size={16} /> Back to Student List
          </button>

          <div>
            <div style={{ color: '#ffffff', fontSize: '15px', fontWeight: '800' }}>
              {studentName} — Marks Card Preview
            </div>
            <div style={{ color: '#94a3b8', fontSize: '12px' }}>
              Class: {rc.student?.className || rc.className} {rc.student?.section ? `(${rc.student?.section})` : ''} | Term: {rc.term}
            </div>
          </div>
        </div>

        {/* Center: Live Format Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '700' }}>
            Format:
          </span>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            style={{
              padding: '7px 12px',
              fontSize: '13px',
              fontWeight: '700',
              borderRadius: '6px',
              background: '#0f172a',
              color: '#ffffff',
              border: '1.5px solid #3b82f6',
              cursor: 'pointer'
            }}
          >
            {MARKS_CARD_FORMATS.map(f => (
              <option key={f.key} value={f.key}>
                {f.icon} {f.name}
              </option>
            ))}
          </select>

          {cardSavedFormat && format !== cardSavedFormat && (
            <button
              type="button"
              onClick={handleApplyTemplateToCard}
              disabled={savingTemplate}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '6px 12px',
                borderRadius: '6px',
                background: '#2563eb',
                color: '#ffffff',
                border: 'none',
                fontSize: '12px',
                fontWeight: '700',
                cursor: savingTemplate ? 'not-allowed' : 'pointer'
              }}
              title="Save this template format permanently to this report card in DB"
            >
              <Save size={13} /> {savingTemplate ? 'Saving...' : 'Save Format to Card'}
            </button>
          )}

          {templateSavedMsg && (
            <span style={{ color: '#4ade80', fontSize: '11px', fontWeight: '700' }}>
              ✓ {templateSavedMsg}
            </span>
          )}
        </div>

        {/* Right: Zoom & Export Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Zoom buttons */}
          <div style={{ display: 'flex', background: '#334155', borderRadius: '6px', padding: '2px' }}>
            <button
              type="button"
              onClick={() => setZoomLevel(prev => Math.max(0.6, prev - 0.1))}
              style={{ background: 'none', border: 'none', color: '#cbd5e1', padding: '6px 8px', cursor: 'pointer' }}
              title="Zoom Out"
            >
              <ZoomOut size={14} />
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel(1.0)}
              style={{ background: 'none', border: 'none', color: '#ffffff', padding: '6px 8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
            >
              {Math.round(zoomLevel * 100)}%
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel(prev => Math.min(1.5, prev + 0.1))}
              style={{ background: 'none', border: 'none', color: '#cbd5e1', padding: '6px 8px', cursor: 'pointer' }}
              title="Zoom In"
            >
              <ZoomIn size={14} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setTeacherRemarks(rc.classTeacherRemarks || '');
              setPrincipalRemarks(rc.principalRemarks || '');
              setRemarksMsg('');
              setIsEditingRemarks(true);
            }}
            style={{
              padding: '8px 14px',
              background: '#047857',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12.5px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Edit3 size={14} /> Edit Remarks
          </button>

          <button
            type="button"
            onClick={handlePrint}
            style={{
              padding: '8px 14px',
              background: '#334155',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12.5px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Printer size={14} /> Print
          </button>

          <button
            type="button"
            onClick={() => onDownloadPDF(rc, format)}
            style={{
              padding: '8px 16px',
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12.5px',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(37,99,235,0.4)'
            }}
          >
            <Download size={14} /> Download PDF
          </button>
        </div>
      </div>

      {/* Remarks Edit Modal */}
      {isEditingRemarks && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(3px)',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            width: '520px',
            maxWidth: '100%',
            padding: '24px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                ✏️ Edit Official Remarks (Save to DB)
              </h3>
              <button
                type="button"
                onClick={() => setIsEditingRemarks(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={18} />
              </button>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                Class Teacher Remarks:
              </label>
              <textarea
                value={teacherRemarks}
                onChange={(e) => setTeacherRemarks(e.target.value)}
                placeholder="Enter teacher observation for this student..."
                rows={3}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '13px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                Principal Observation:
              </label>
              <textarea
                value={principalRemarks}
                onChange={(e) => setPrincipalRemarks(e.target.value)}
                placeholder="Enter principal observation or promotion notes..."
                rows={3}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '13px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {remarksMsg && (
              <div style={{ fontSize: '12px', color: '#dc2626', background: '#fef2f2', padding: '6px 10px', borderRadius: '4px' }}>
                {remarksMsg}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => setIsEditingRemarks(false)}
                style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={savingRemarks}
                onClick={async () => {
                  setSavingRemarks(true);
                  setRemarksMsg('');
                  try {
                    const res = await reportCardService.updateRemarks(rc.id, teacherRemarks, principalRemarks);
                    if (res && res.success) {
                      await loadDetails(rc.id);
                      setIsEditingRemarks(false);
                    } else {
                      setRemarksMsg(res?.error || 'Failed to save remarks to database.');
                    }
                  } catch (err) {
                    setRemarksMsg(err.message || 'Error updating remarks.');
                  } finally {
                    setSavingRemarks(false);
                  }
                }}
                style={{
                  padding: '8px 18px',
                  borderRadius: '6px',
                  background: '#1e3a8a',
                  color: '#ffffff',
                  fontWeight: '700',
                  border: 'none',
                  cursor: savingRemarks ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Check size={15} /> {savingRemarks ? 'Saving to DB...' : 'Save Remarks to DB'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Canvas Viewport */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '30px 20px 60px 20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
      }}>
        {loading ? (
          <div style={{ color: '#94a3b8', fontSize: '14px', marginTop: '60px' }}>
            Preparing high-fidelity marks card rendering...
          </div>
        ) : (
          <div style={{
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'top center',
            transition: 'transform 0.15s ease-out',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            background: '#ffffff',
            borderRadius: '8px'
          }}>
            <MarksCardRenderer
              key={format}
              reportCard={rc}
              entries={entries}
              branding={branding}
              institutionName={orgInfo.orgName || orgData?.orgName}
              institutionAddress={orgInfo.address || orgData?.address}
              template={format}
              templateConfig={activeTemplateConfig}
              gradingScale={gradingScale}
              id="full-screen-preview-render"
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default ReportCardPreview;
